import { HISTOGRAM_EDGES } from "@bt/domain/report";
import { useState } from "react";
import { axis } from "./scale";
import { useWidth } from "./use-width";

/**
 * A bucket's label from its lower edge and the next: "0", "2–5", "1,001+" for counts; for a
 * continuous measure (time, distance) the next bucket starts where this one ends: "1 min–5 min".
 */
export function bucketLabel(
  edges: readonly number[],
  i: number,
  format: (n: number) => string = String,
  continuous = false,
): string {
  const low = edges[i] ?? 0;
  const next = edges[i + 1];
  if (next === undefined) return `${format(low)}+`;
  if (continuous) return `${format(low)}–${format(next)}`;
  return next - 1 === low ? format(low) : `${format(low)}–${format(next - 1)}`;
}

/** The axis's short label: a continuous bucket by where it starts, the last with "+". */
function axisLabel(
  edges: readonly number[],
  i: number,
  format: (n: number) => string,
  continuous: boolean,
) {
  if (!continuous || i === edges.length - 1) return bucketLabel(edges, i, format, continuous);
  return format(edges[i] ?? 0);
}

/** About how wide a label of 10px text is. */
const labelWidth = (text: string) => text.length * 5.6 + 8;

const MARGIN = { top: 8, right: 4, bottom: 24, left: 40 };

/**
 * Counts per bucket on fixed edges (the report's `HISTOGRAM_EDGES` unless told otherwise), so
 * histograms from different months compare. One hue, thin columns with a 2px gap, rounded at the
 * top only; each column shows its count on hover and focus.
 */
export function Histogram({
  counts,
  edges = HISTOGRAM_EDGES,
  formatCount,
  formatEdge = String,
  continuous = false,
  label,
  height = 160,
}: {
  counts: readonly number[];
  edges?: readonly number[];
  formatCount: (n: number) => string;
  formatEdge?: (n: number) => string;
  /** Time or distance rather than a count: see `bucketLabel`. */
  continuous?: boolean;
  label: string;
  height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const { top, ticks } = axis(Math.max(0, ...counts), { count: 3, integer: true });
  const plotWidth = Math.max(10, width - MARGIN.left - MARGIN.right);
  const plotHeight = height - MARGIN.top - MARGIN.bottom;
  const slot = plotWidth / Math.max(1, counts.length);
  const bar = Math.min(24, slot - 2);
  const y = (v: number) => MARGIN.top + plotHeight - (v / top) * plotHeight;
  const radius = 4;
  // Every bucket is labelled when the labels fit; otherwise every second or third one.
  const labels = counts.map((_, i) => axisLabel(edges, i, formatEdge, continuous));
  const every = Math.max(1, Math.ceil(Math.max(0, ...labels.map(labelWidth)) / slot));

  return (
    <div ref={ref} className="relative">
      <svg role="img" aria-label={label} width={width} height={height} className="block">
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={MARGIN.left + plotWidth}
              y1={y(tick)}
              y2={y(tick)}
              stroke={tick === 0 ? "var(--chart-axis)" : "var(--border)"}
            />
            <text
              x={MARGIN.left - 8}
              y={y(tick)}
              dy="0.32em"
              textAnchor="end"
              className="fill-muted-foreground text-[11px] tabular"
            >
              {formatCount(tick)}
            </text>
          </g>
        ))}
        {counts.map((count, i) => {
          const cx = MARGIN.left + slot * i + slot / 2;
          const h = count > 0 ? Math.max(2, plotHeight - (y(count) - MARGIN.top)) : 0;
          const r = Math.min(radius, h / 2, bar / 2);
          const left = cx - bar / 2;
          const base = MARGIN.top + plotHeight;
          const path =
            h > 0
              ? `M${left},${base}V${base - h + r}Q${left},${base - h} ${left + r},${base - h}H${left + bar - r}Q${left + bar},${base - h} ${left + bar},${base - h + r}V${base}Z`
              : "";
          return (
            <g
              key={edges[i] ?? i}
              onPointerEnter={() => setActive(i)}
              onPointerLeave={() => setActive(null)}
            >
              <rect
                x={cx - slot / 2}
                y={MARGIN.top}
                width={slot}
                height={plotHeight}
                fill="transparent"
              />
              {path ? (
                <path
                  d={path}
                  fill="var(--chart-1)"
                  opacity={active === null || active === i ? 1 : 0.55}
                />
              ) : null}
              {i % every === 0 ? (
                <text
                  x={cx}
                  y={height - 6}
                  textAnchor="middle"
                  className="fill-muted-foreground text-[10px]"
                >
                  {labels[i]}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {active !== null ? (
        <div
          role="status"
          className="pointer-events-none absolute top-0 rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 text-xs shadow-md"
          style={{ left: Math.min(width - 120, MARGIN.left + slot * active) }}
        >
          <span className="font-semibold tabular">{formatCount(counts[active] ?? 0)}</span>{" "}
          <span className="text-muted-foreground">
            {bucketLabel(edges, active, formatEdge, continuous)}
          </span>
        </div>
      ) : null}
    </div>
  );
}
